import React, { useMemo } from 'react';

// The Functions window is a container-agnostic "all launchers" grid. A
// WindowManager passes its full launcher list to every child window as
// `programs` (see WindowManager render: programs={programs}) and the opener
// as `onOpenWindow`, so wherever this app is registered it lists and opens
// that container's own programs — e.g. inside Money Manager it shows
// Portfolio / Exchange / Send / …, and inside Security Tools it shows
// Seed Phrase / Hunter / Split / … even when the toolbar is icon-only.
// Reuses the Program Manager group look (.program-icon).

// Never list ourselves, never list pure menu/openLevel stubs that have
// no component to render, and never list dev-only tools (test
// harnesses etc.) that mark themselves `hideInFunctions` — this grid
// is the user-facing "all apps" launcher, not a debug menu.
const SELF_PROGNAME = 'functions.exe';

const FunctionsApp = ({ programs, subPrograms, onOpenWindow, parentOnOpenWindow, programData }) => {
	const source = (Array.isArray(programs) && programs.length > 0) ? programs : (subPrograms || []);

	const launchers = useMemo(() => (source || []).filter((program) => (
		program
		&& !!program.component
		&& program.menuOnly !== true
		&& program.hideInFunctions !== true
		&& program.progName !== SELF_PROGNAME
	)), [source]);

	const open = (program) => {
		if (!program) return;
		if (typeof onOpenWindow === 'function') {
			onOpenWindow(program, programData || {});
			return;
		}
		if (typeof parentOnOpenWindow === 'function') {
			parentOnOpenWindow(program, programData || {}, true);
		}
	};

	if (launchers.length === 0) {
		return (
			<div className="functions-app">
				<p className="functions-app__empty">No functions are available in this workspace.</p>
			</div>
		);
	}

	return (
		<div className="functions-app">
			<div className="program-manager functions-app__grid">
				{launchers.map((program, index) => {
					const key = program.progName || `fn-${index}`;
					const iconClass = program.progName ? `icon_${program.progName.split('.')[0]}` : '';
					return (
						<div
							key={key}
							className={`program-icon functions-app__item ${iconClass}`.trim()}
							role="button"
							tabIndex={0}
							onClick={() => open(program)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									open(program);
								}
							}}
							title={program.tooltip || program.title}
						>
							<div className="functions-app__icon">{program.icon}</div>
							<div className="functions-app__label">{program.title}</div>
							{program.titleNote ? (
								<div className="functions-app__note">{program.titleNote}</div>
							) : null}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default FunctionsApp;
